import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Toaster } from "sonner";
import {
  Truck,
  Package,
  CheckCircle,
  ArrowRight,
  Receipt,
  MapPin,
  Stack,
  Lightning,
  Star,
  Rocket,
  Check,
  ShieldCheck,
  CreditCard,
  Tag,
} from "@phosphor-icons/react";
import axios from "axios";

import { useBranding } from "@/context/BrandingContext";
import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";
const PLAN_ICONS = { starter: Star, pro: Lightning, scale: Rocket };

export default function Landing() {
  const navigate = useNavigate();
  const branding = useBranding();
  const [plans, setPlans] = useState([]);
  const [trackInput, setTrackInput] = useState("");
  // Active promos surfaced as a top-of-page banner. Public endpoint — no auth needed.
  const [promos, setPromos] = useState([]);

  const onTrackSubmit = (e) => {
    e.preventDefault();
    const code = (trackInput || "").trim().toUpperCase();
    if (!code) return;
    navigate(`/track/${encodeURIComponent(code)}`);
  };

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/billing/plans`)
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => {});
    axios
      .get(`${BACKEND}/api/public/promotions/active`)
      .then((r) => setPromos(r.data.items || []))
      .catch(() => {});
  }, []);

  // White-label mode: the customer typed the courier's branded domain (e.g.
  // app.swiftcourier.com). Render a stripped landing page with ONLY this
  // courier's branding + sign in / sign up CTAs. No Kanal marketing surface.
  if (branding.isWhiteLabel) {
    return <BrandedLanding branding={branding} />;
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="landing-page">
      <Toaster position="top-right" />

      {/* Top navigation */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/brand/kanal-logo.svg" alt="Kanal" className="h-9 w-9 bg-zinc-950 rounded p-1" />
            <div className="leading-tight">
              <div className="font-semibold tracking-tight text-lg" style={{ fontFamily: "Work Sans" }}>Kanal</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500"><a href="https://cyrusfieldtech.com" target="_blank">Cyrus Field Tech</a></div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-zinc-600">
            <a href="#features" className="hover:text-zinc-900 transition-colors" data-testid="nav-features">Features</a>
            <a href="#pricing" className="hover:text-zinc-900 transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#audiences" className="hover:text-zinc-900 transition-colors" data-testid="nav-audiences">Who it's for</a>
            <Link to="/driver/signup" className="text-amber-700 hover:text-amber-900 font-medium transition-colors inline-flex items-center gap-1" data-testid="nav-drive">
              <Lightning size={12} weight="fill" /> Deliver for us
            </Link>
            <Link to="/login?role=driver" className="text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="nav-driver-signin">
              Driver sign-in
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="signin-link">
              <Button variant="ghost" size="sm" className="text-sm">Sign in</Button>
            </Link>
            <Link to="/signup/courier" data-testid="hero-cta-signup-courier">
              <Button size="sm" className="bg-[#002BF6] hover:bg-[#0021C7] text-white text-sm">Start courier trial</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Active promo banner — new courier signups can use this code on the signup form. */}
      {promos.length > 0 && (
        <PromoBanner promos={promos} />
      )}

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-50 via-white to-white pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#002BF6] bg-[#002BF6]/8 border border-[#002BF6]/15 px-3 py-1.5 rounded-full mb-6">
              <Lightning size={11} weight="bold" /> The Operating System for Courier & Logistics Companies Worldwide
            </div>
            <h1 className="text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-6" style={{ fontFamily: "Work Sans" }}>
              Run a smarter courier company.{" "}
              <span className="text-[#002BF6]">Get paid faster.</span>
            </h1>
            <p className="text-lg text-zinc-600 mb-8 max-w-[560px] leading-relaxed">
              Kanal is the all-in-one hyperscaler logistics platform built for courier and logistics businesses across the Caribbean, Canada, the United States, South America, and Africa. Track every package, manage drivers and manifests, send branded invoices, and accept card and local payments — all in one workspace.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <Link to="/signup/courier" data-testid="hero-cta-courier">
                <Button size="lg" className="bg-[#002BF6] hover:bg-[#0021C7] text-white h-12 px-7">
                  <Truck size={18} weight="bold" className="mr-2" />
                  Start as a courier company
                  <ArrowRight size={14} weight="bold" className="ml-2" />
                </Button>
              </Link>
              <a href="#audiences" data-testid="hero-cta-customer-anchor">
                <Button size="lg" variant="outline" className="h-12 px-7">
                  <Package size={18} weight="bold" className="mr-2" />
                  I'm a customer — track my packages
                </Button>
              </a>
            </div>

            {/* Public tracking — no login required */}
            <form onSubmit={onTrackSubmit} className="mt-2 mb-8 max-w-[520px]" data-testid="hero-track-form">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">
                Already shipped? Track your package
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={trackInput}
                  onChange={(e) => setTrackInput(e.target.value.toUpperCase())}
                  placeholder="KN-12345"
                  className="font-mono flex-1 h-14 sm:h-11 px-3.5 rounded-md border border-zinc-300 bg-white text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#002BF6]/30 focus:border-[#002BF6]"
                  data-testid="hero-track-input"
                />
                <Button
                  type="submit"
                  className="bg-zinc-900 hover:bg-zinc-800 text-white h-11 px-5"
                  data-testid="hero-track-submit"
                >
                  <Package size={14} weight="bold" className="mr-1.5" /> Track
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                No account needed. Tracking numbers start with <code className="font-mono bg-zinc-100 px-1 py-0.5 rounded">KN-</code>.
              </p>
            </form>
            <div className="flex items-center gap-6 text-xs text-zinc-500">
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="bold" className="text-emerald-600" /> No credit card to start</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="bold" className="text-emerald-600" /> 30-day plan cycle</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="bold" className="text-emerald-600" /> Multi-region support</div>
            </div>
          </div>

          {/* Hero card: tracking mock */}
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#002BF6]/10 to-emerald-500/10 blur-2xl rounded-3xl" />
            <div className="relative bg-white border border-zinc-200 rounded-xl shadow-xl shadow-zinc-900/5 overflow-hidden">
              <div className="bg-zinc-950 text-zinc-100 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-mono">
                  <Package size={14} weight="bold" />
                  KN1029384756
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full">In transit</span>
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Recipient</div>
                <div className="font-semibold text-zinc-900 mb-4">A. Brown — Kingston, JM</div>
                <div className="space-y-3">
                  {[
                    { ok: true, label: "Picked up at MIA Hub", time: "Mon 9:12 AM" },
                    { ok: true, label: "Cleared customs", time: "Tue 3:48 PM" },
                    { ok: true, label: "Out for delivery", time: "Today 8:02 AM" },
                    { ok: false, label: "Delivered", time: "Pending" },
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`mt-1 w-4 h-4 rounded-full flex items-center justify-center ${step.ok ? "bg-emerald-500" : "bg-zinc-200"}`}>
                        {step.ok && <Check size={10} weight="bold" className="text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className={`text-sm ${step.ok ? "text-zinc-900 font-medium" : "text-zinc-400"}`}>{step.label}</div>
                        <div className="text-xs text-zinc-500">{step.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-zinc-100 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500">Balance due</div>
                    <div className="text-lg font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }}>$2,450.00 <span className="text-xs font-normal text-zinc-500">JMD</span></div>
                  </div>
                  <Button size="sm" className="bg-[#002BF6] hover:bg-[#0021C7] text-white"><CreditCard size={13} className="mr-1.5" weight="bold" /> Pay now</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section id="audiences" className="border-t border-zinc-100 bg-zinc-50/50">
        <div className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/signup/courier" className="group block bg-white border border-zinc-200 rounded-xl p-8 hover:border-[#002BF6] hover:shadow-lg hover:shadow-blue-500/5 transition-all" data-testid="audience-courier-card">
              <div className="w-12 h-12 bg-[#002BF6] text-white rounded-lg flex items-center justify-center mb-5">
                <Truck size={22} weight="bold" />
              </div>
              <h3 className="text-2xl font-semibold tracking-tight mb-3" style={{ fontFamily: "Work Sans" }}>I'm a courier company</h3>
              <p className="text-zinc-600 mb-5">
                Manage packages, manifests, drivers, branches and invoicing across your team. Get a permanent customer signup URL on day one.
              </p>
              <ul className="space-y-2 text-sm text-zinc-700 mb-6">
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Multi-role team (admin / dispatcher / viewer)</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Air / sea / ground manifests</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Driver auto-assignment with geocoding</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Auto-generated PDF labels &amp; invoices</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Branded customer signup page included</li>
              </ul>
              <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#002BF6] group-hover:gap-2.5 transition-all">
                Start courier trial <ArrowRight size={14} weight="bold" />
              </div>
            </Link>

            <div className="bg-white border border-zinc-200 rounded-xl p-8" data-testid="audience-customer-card">
              <div className="w-12 h-12 bg-emerald-500 text-white rounded-lg flex items-center justify-center mb-5">
                <Package size={22} weight="bold" />
              </div>
              <h3 className="text-2xl font-semibold tracking-tight mb-3" style={{ fontFamily: "Work Sans" }}>I'm a courier customer</h3>
              <p className="text-zinc-600 mb-5">
                Your courier company has a unique Kanal signup URL — usually shared in their welcome email or printed on your invoice. Sign up there to:
              </p>
              <ul className="space-y-2 text-sm text-zinc-700 mb-6">
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Track every package in real time</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Pay invoices online via WiPay (JMD)</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> See package history &amp; proof of delivery</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> One login for all your shipments</li>
              </ul>
              <p className="text-xs text-zinc-500 mb-4">
                Don't have your courier's signup URL? Ask them, or sign in if you already have an account.
              </p>
              <div className="flex gap-2">
                <Link to="/login" data-testid="audience-customer-signin">
                  <Button variant="outline" size="sm">Sign in</Button>
                </Link>
              </div>
            </div>

            <Link to="/driver/signup" className="group block bg-gradient-to-br from-amber-50 via-yellow-50 to-white border border-amber-200 rounded-xl p-8 hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/10 transition-all" data-testid="audience-driver-card">
              <ZipExpressLogoTile />
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 mb-1">ZipExpress · Last-mile network</div>
              <h3 className="text-2xl font-semibold tracking-tight mb-3" style={{ fontFamily: "Work Sans" }}>I want to drive &amp; earn</h3>
              <p className="text-zinc-600 mb-5">
                Sign up to deliver ground packages across the island for Kanal&apos;s courier network. Keep <b>86% of every delivery fee</b>. Get paid every Friday.
              </p>
              <ul className="space-y-2 text-sm text-zinc-700 mb-6">
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Jobs auto-assigned by proximity</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Set your own vehicle &amp; capacity</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Live payout tracker in your portal</li>
                <li className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Mobile-first app — no install needed</li>
              </ul>
              <div className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm shadow-amber-500/30 transition-colors">
                Apply to deliver for ZipExpress <ArrowRight size={14} weight="bold" />
              </div>
            </Link>
          </div>
          <div className="text-center mt-6 text-xs text-zinc-500">
            Already a verified driver?{" "}
            <Link to="/login" className="text-amber-700 hover:text-amber-900 font-semibold" data-testid="audiences-driver-signin">
              Sign in to your portal 
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-zinc-100">
        <div className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Built for courier operations</div>
            <h2 className="text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: "Work Sans" }}>Everything you need, in one workspace</h2>
            <p className="text-zinc-600">From the moment a package arrives at your hub to the moment it's delivered and paid, Kanal handles the chain end-to-end.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { Icon: Package, title: "Package tracking", text: "KN-prefixed tracking numbers, exception alerts, status timelines, and hub trail scans." },
              { Icon: Stack, title: "Air / Sea / Ground manifests", text: "Sealed packing lists with auto-PDF, status workflow and child-package state propagation." },
              { Icon: Truck, title: "Driver auto-assignment", text: "Automated route optimisation, Geocoded distance ranking, capacity limits in kg & lb, active-load count per driver." },
              { Icon: MapPin, title: "Branches & hubs", text: "Multi-hub support with scan trails — visible to the customer in real time." },
              { Icon: Receipt, title: "Invoicing + WiPay payments", text: "Branded PDFs, email-with-Pay-Now-CTA, JMD or USD, locked accounts on unpaid initial invoices." },
              { Icon: ShieldCheck, title: "RBAC & audit log", text: "Super admin / company admin / dispatcher / viewer / customer — with full audit trail." },
            ].map((f) => (
              <div key={f.title} className="bg-white border border-zinc-200 rounded-xl p-6 hover:border-zinc-300 transition-colors">
                <div className="w-10 h-10 bg-zinc-950 text-white rounded-lg flex items-center justify-center mb-4">
                  <f.Icon size={18} weight="bold" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-zinc-100 bg-zinc-50/50">
        <div className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Pricing in USD</div>
            <h2 className="text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: "Work Sans" }}>Simple, transparent plans</h2>
            <p className="text-zinc-600">All plans billed monthly in USD. Cancel any time. Pay with credit card or via WiPay.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-[1100px] mx-auto" data-testid="pricing-grid">
            {plans.map((p) => {
              const Icon = PLAN_ICONS[p.id] || Star;
              const featured = p.id === "pro";
              return (
                <div
                  key={p.id}
                  className={`relative rounded-xl p-7 bg-white border transition-all ${featured ? "border-[#002BF6] shadow-xl shadow-blue-500/10 md:-translate-y-2" : "border-zinc-200"}`}
                  data-testid={`landing-plan-${p.id}`}
                >
                  {featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#002BF6] text-white text-[10px] uppercase tracking-wider px-3 py-1 rounded-full font-semibold">Most popular</div>
                  )}
                  <div className="flex items-center gap-2 mb-5">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${featured ? "bg-[#002BF6] text-white" : "bg-zinc-100 text-zinc-700"}`}>
                      <Icon size={17} weight="bold" />
                    </div>
                    <div className="text-sm font-semibold uppercase tracking-wider text-zinc-900">{p.name}</div>
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Work Sans" }}>${p.price}</span>
                    <span className="text-sm text-zinc-500">/mo</span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-5">USD per month</div>
                  <p className="text-sm text-zinc-600 mb-6">{p.description}</p>
                  <ul className="space-y-2.5 text-sm text-zinc-700 mb-7">
                    {p.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2">
                        <Check size={14} weight="bold" className="text-emerald-600 mt-0.5 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/signup/courier?plan=${p.id}`} data-testid={`pricing-cta-${p.id}`}>
                    <Button className={`w-full ${featured ? "bg-[#002BF6] hover:bg-[#0021C7] text-white" : ""}`} variant={featured ? "default" : "outline"}>
                      Start with {p.name}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2.5 text-zinc-500">
            <img src="/brand/kanal-logo.svg" alt="Kanal" className="h-7 w-7 bg-zinc-950 rounded p-0.5" />
            <span className="font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }}>Kanal</span>
            <span>·</span>
            <span><a href="https://cyrusfieldtech.com" target="_blank">Cyrus Field Tech</a></span>
            <span>·</span>
            <span>Jamaica</span>
          </div>
          <div className="flex gap-5 text-zinc-500">
            <Link to="/login" className="hover:text-zinc-900 transition-colors">Sign in</Link>
            <Link to="/signup/courier" className="hover:text-zinc-900 transition-colors">Courier signup</Link>
            <Link to="/docs" className="hover:text-zinc-900 transition-colors" data-testid="footer-api-docs-link">API Docs</Link>
            <a href="mailto:hello@cyrusfieldtech.com" className="hover:text-zinc-900 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}


/**
 * Active-promo banner shown at the top of the landing page. Targeted at new
 * signups; the code is meant to be entered on the courier signup form.
 */
function PromoBanner({ promos }) {
  const top = promos[0];
  if (!top) return null;
  const plansLabel = (top.applies_to_plan_ids || []).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" or ");
  const endsStr = (() => {
    try {
      return new Date(top.end_date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return top.end_date; }
  })();
  return (
    <div
      className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-b border-amber-200"
      data-testid="landing-promo-banner"
    >
      <div className="max-w-[1200px] mx-auto px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start sm:items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-sm">
            <Tag size={15} weight="bold" />
          </div>
          <div className="text-sm">
            <span className="font-semibold text-amber-900">
              {top.discount_pct}% off {plansLabel || "Pro or Scale"}
            </span>
            <span className="text-amber-800/80">
              {top.description ? ` — ${top.description}` : " for first-time courier companies."}
            </span>
            <span className="text-amber-700/70 text-xs ml-2">Ends {endsStr}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <code
            className="text-xs font-mono font-bold bg-white text-amber-900 px-2.5 py-1 rounded border border-amber-300"
            data-testid="landing-promo-code"
          >
            {top.code}
          </code>
          <Link
            to={`/signup/courier?promo=${encodeURIComponent(top.code)}`}
            data-testid="landing-promo-cta"
            className="text-xs font-semibold text-amber-900 bg-amber-200 hover:bg-amber-300 transition-colors px-3 py-1.5 rounded inline-flex items-center gap-1"
          >
            Claim
            <ArrowRight size={11} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}



/**
 * ZipExpress logo tile shown on the homepage driver-signup card.
 * Loads the courier-uploaded brand logo from `/api/zipexpress/logo` and falls
 * back to the lightning-bolt gradient tile if the logo isn't configured yet.
 */
function ZipExpressLogoTile() {
  const [logoOk, setLogoOk] = useState(true);
  if (logoOk) {
    return (
      <div className="w-14 h-14 rounded-lg bg-white border border-amber-200 flex items-center justify-center mb-5 shadow-sm overflow-hidden">
        <img
          src={`${BACKEND}/api/zipexpress/logo`}
          alt="ZipExpress"
          className="w-full h-full object-contain p-1.5"
          onError={() => setLogoOk(false)}
          data-testid="landing-zipx-logo"
        />
      </div>
    );
  }
  return (
    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 text-white rounded-lg flex items-center justify-center mb-5 shadow-sm">
      <Lightning size={22} weight="fill" />
    </div>
  );
}


/**
 * Branded landing for a courier company's custom domain (e.g. app.swiftcourier.com).
 * Shows ONLY this courier's logo + sign in / sign up CTAs — no Kanal-platform
 * marketing surface, no "register your own courier" tile.
 */
function BrandedLanding({ branding }) {
  const logoUrl = branding.logoPath ? `/api/files/${branding.logoPath}` : null;
  const signupHref = branding.publicSlug ? `/c/${branding.publicSlug}/signup` : "/login";
  return (
    <div
      className="min-h-screen bg-white text-zinc-900 flex flex-col"
      data-testid="branded-landing"
    >
      <header className="border-b border-zinc-100">
        <div className="max-w-[1100px] mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={branding.companyName} className="h-10 w-10 rounded-md object-contain bg-zinc-50 border border-zinc-100" />
            ) : (
              <div className="h-10 w-10 rounded-md bg-zinc-900 text-white flex items-center justify-center text-sm font-bold">
                {(branding.companyName || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="leading-tight">
              <div className="font-semibold tracking-tight text-lg" style={{ fontFamily: "Work Sans" }}>
                {branding.companyName}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Courier portal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="branded-signin-link">
              <Button variant="ghost" size="sm" className="text-sm">Sign in</Button>
            </Link>
            <Link to={signupHref} data-testid="branded-signup-link">
              <Button size="sm" className="bg-[#002BF6] hover:bg-[#0021C7] text-white text-sm">Sign up</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="max-w-[800px] mx-auto px-6 py-20 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
            Welcome to
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-4" style={{ fontFamily: "Work Sans" }}>
            {branding.companyName}
          </h1>
          <p className="text-zinc-500 text-lg mb-10">
            Track shipments, manage your packages, and pay invoices — all in one place.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to={signupHref} data-testid="branded-cta-signup">
              <Button size="lg" className="bg-[#002BF6] hover:bg-[#0021C7] text-white h-12 px-6">
                Create your account
              </Button>
            </Link>
            <Link to="/login" data-testid="branded-cta-signin">
              <Button size="lg" variant="outline" className="h-12 px-6">
                Sign in
              </Button>
            </Link>
          </div>
          <div className="mt-12">
            <Link to="/track" className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="branded-track-link">
              Have a tracking number? Track a package →
            </Link>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-100 py-6 text-center text-xs text-zinc-400">
        © {new Date().getFullYear()} {branding.companyName}
      </footer>
    </div>
  );
}
